/**
 * «Тема ≠ категория» (#845, спека §2.1): тема разговора не повторяет категорию
 * звонка (`call_category`) и предмет вопроса клиента (`subject_code`, #843).
 * «Жалобы», «запись на приём», «оплата», «цена» — не темы: это уже есть в
 * других измерениях «Голоса клиента».
 *
 * Одна таблица названий на все пути (спека §2.1):
 * - ручной ввод организации — ПРЕДУПРЕЖДЕНИЕ, не запрет (PR 2);
 * - стартовое предложение (PR 4) и недельная сводка (PR 5) — отсев кандидата
 *   до показа организации.
 *
 * Совпадение — равенство ключей `normalizeThemeKey`, а не вхождение подстроки:
 * «доставка» повторяет предмет вопроса, «доставка в регионы» — уже тема
 * (пример из §2.1). Названия — на четырёх языках продукта: показанное в
 * интерфейсе имя категории или предмета плюс их обиходные формы (множественное
 * число, короткое слово). Тест ui-worker сверяет, что каждое показанное
 * интерфейсом имя здесь есть.
 *
 * Без импорта значений из `@aica/api-types` (кольцо core ↔ api-types, #1076):
 * полноту держат типы — `Record` по коду категории и по коду предмета.
 */
import type { AnalyzedCallCategory, QuestionSubjectCode } from '@aica/api-types/schemas';
import type { Language } from '../i18n/types';
import { normalizeThemeKey } from './theme-key';

type Terms = Readonly<Record<Language, readonly string[]>>;

export const THEME_OVERLAP_CALL_CATEGORY_TERMS: Readonly<
  Record<AnalyzedCallCategory, Terms>
> = {
  product_inquiry: {
    en: ['product inquiry', 'product inquiries', 'product question', 'inquiry'],
    ru: ['запрос о продукте', 'запросы о продукте', 'вопрос о продукте'],
    uk: ['запит про продукт', 'запити про продукт', 'питання про продукт'],
    es: ['consulta de producto', 'consultas de producto', 'consulta'],
  },
  purchase: {
    en: ['purchase', 'purchases', 'buying', 'sale', 'sales'],
    ru: ['покупка', 'покупки', 'продажа', 'продажи'],
    uk: ['покупка', 'покупки', 'купівля', 'продаж', 'продажі'],
    es: ['compra', 'compras', 'venta', 'ventas'],
  },
  scheduling: {
    en: ['scheduling', 'appointment', 'appointments', 'booking'],
    ru: [
      'запись',
      'запись на приём',
      'запись / планирование',
      'планирование',
      'встреча',
      'встречи',
    ],
    uk: [
      'запис',
      'запис на прийом',
      'запис / планування',
      'планування',
      'зустріч',
      'зустрічі',
    ],
    es: ['programación', 'cita', 'citas', 'reserva', 'reservas'],
  },
  support: {
    en: ['support', 'customer support', 'technical support', 'tech support'],
    ru: ['поддержка', 'техподдержка', 'техническая поддержка'],
    uk: ['підтримка', 'техпідтримка', 'технічна підтримка'],
    es: ['soporte', 'soporte técnico', 'asistencia'],
  },
  billing: {
    en: ['billing', 'payment', 'payments', 'invoice', 'invoices'],
    ru: ['оплата', 'платёж', 'платежи', 'счёт', 'счета'],
    uk: ['оплата', 'платіж', 'платежі', 'рахунок', 'рахунки'],
    es: ['facturación', 'pago', 'pagos', 'factura', 'facturas'],
  },
  onboarding: {
    en: ['onboarding', 'setup', 'getting started'],
    ru: ['онбординг', 'подключение', 'внедрение'],
    uk: ['онбординг', 'підключення', 'впровадження'],
    es: ['incorporación', 'onboarding', 'alta'],
  },
  complaint: {
    en: ['complaint', 'complaints'],
    ru: ['жалоба', 'жалобы', 'претензия', 'претензии'],
    uk: ['скарга', 'скарги', 'претензія', 'претензії'],
    es: ['queja', 'quejas', 'reclamación', 'reclamaciones', 'reclamo', 'reclamos'],
  },
  cancellation: {
    en: ['cancellation', 'cancellations', 'cancel'],
    ru: ['отмена', 'отмены', 'расторжение'],
    uk: ['скасування', 'розірвання'],
    es: ['cancelación', 'cancelaciones', 'baja', 'anulación'],
  },
  follow_up: {
    en: ['follow up', 'follow-up', 'followup', 'repeat contact'],
    ru: ['повторный контакт', 'повторное обращение', 'повторный звонок'],
    uk: ['повторний контакт', 'повторне звернення', 'повторний дзвінок'],
    es: ['seguimiento', 'contacto repetido'],
  },
  other: {
    en: ['other', 'miscellaneous'],
    ru: ['другое', 'прочее', 'разное'],
    uk: ['інше', 'різне'],
    es: ['otro', 'otros', 'varios'],
  },
};

export const THEME_OVERLAP_QUESTION_SUBJECT_TERMS: Readonly<
  Record<QuestionSubjectCode, Terms>
> = {
  price_and_inclusions: {
    en: ['price', 'prices', 'pricing', 'cost', 'price and what it includes'],
    ru: ['цена', 'цены', 'стоимость', 'цена и что в неё входит'],
    uk: ['ціна', 'ціни', 'вартість', 'ціна і що до неї входить'],
    es: ['precio', 'precios', 'coste', 'costo', 'precio y qué incluye'],
  },
  payment_and_financing: {
    en: ['payment and financing', 'financing'],
    ru: ['оплата и финансирование', 'финансирование'],
    uk: ['оплата і фінансування', 'фінансування'],
    es: ['pago y financiación', 'financiación', 'financiamiento'],
  },
  delivery_and_timing: {
    en: ['delivery', 'delivery and timing', 'shipping', 'timing'],
    ru: ['доставка', 'доставка и сроки', 'сроки', 'сроки доставки'],
    uk: ['доставка', 'доставка і терміни', 'терміни', 'терміни доставки'],
    es: ['entrega', 'entrega y plazos', 'envío', 'plazos'],
  },
  availability: {
    en: ['availability', 'in stock'],
    ru: ['наличие', 'наличие товара'],
    uk: ['наявність', 'наявність товару'],
    es: ['disponibilidad', 'existencias'],
  },
  specs_and_configuration: {
    en: ['specifications', 'specs', 'configuration', 'specifications and configuration'],
    ru: ['характеристики', 'комплектация', 'характеристики и комплектация'],
    uk: ['характеристики', 'комплектація', 'характеристики і комплектація'],
    es: [
      'características',
      'configuración',
      'características y configuración',
      'especificaciones',
    ],
  },
  warranty_and_service: {
    en: ['warranty', 'warranty and service', 'guarantee'],
    ru: ['гарантия', 'гарантия и сервис'],
    uk: ['гарантія', 'гарантія і сервіс'],
    es: ['garantía', 'garantía y servicio'],
  },
  documents: {
    en: ['documents', 'documentation', 'paperwork'],
    ru: ['документы', 'документация'],
    uk: ['документи', 'документація'],
    es: ['documentos', 'documentación'],
  },
  comparison: {
    en: ['comparison', 'comparisons'],
    ru: ['сравнение'],
    uk: ['порівняння'],
    es: ['comparación', 'comparativa'],
  },
  purchase_process: {
    en: ['how to buy', 'purchase process', 'buying process'],
    ru: ['как купить', 'процесс покупки'],
    uk: ['як купити', 'процес покупки', 'процес купівлі'],
    es: ['cómo comprar', 'proceso de compra'],
  },
  other: {
    en: ['other questions'],
    ru: ['другие вопросы'],
    uk: ['інші питання'],
    es: ['otras preguntas'],
  },
};

export type ThemeOverlapDimension = 'call_category' | 'question_subject';

export interface ThemeOverlap {
  dimension: ThemeOverlapDimension;
  /** Код категории звонка или предмета вопроса. */
  code: string;
}

/** Ключ названия → совпавшие измерения; строится один раз на модуль. */
let overlapIndexCache: Map<string, ThemeOverlap[]> | undefined;

function overlapIndex(): Map<string, ThemeOverlap[]> {
  if (overlapIndexCache) return overlapIndexCache;
  const built = new Map<string, ThemeOverlap[]>();
  const add = (
    dimension: ThemeOverlapDimension,
    table: Readonly<Record<string, Terms>>
  ): void => {
    for (const [code, byLanguage] of Object.entries(table)) {
      for (const terms of Object.values(byLanguage)) {
        for (const term of terms) {
          const key = normalizeThemeKey(term);
          const list = built.get(key) ?? [];
          if (!list.some((o) => o.dimension === dimension && o.code === code)) {
            list.push({ dimension, code });
          }
          built.set(key, list);
        }
      }
    }
  };
  add('call_category', THEME_OVERLAP_CALL_CATEGORY_TERMS);
  add('question_subject', THEME_OVERLAP_QUESTION_SUBJECT_TERMS);
  overlapIndexCache = built;
  return built;
}

/**
 * Какие категории звонка и предметы вопроса повторяет название темы (по ключу
 * `normalizeThemeKey`, на любом из четырёх языков). Пусто — не повторяет.
 * Порядок: категории, затем предметы — в порядке таблиц.
 */
export function findThemeOverlaps(name: string): ThemeOverlap[] {
  const key = normalizeThemeKey(name);
  if (!key) return [];
  return [...(overlapIndex().get(key) ?? [])];
}
